import React from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { RiskConsequences, RiskEvents, RiskSources } from 'src/api/types';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName } from 'src/api/types/options';
import { SparkleSvg, RefreshSvg } from 'src/assets/dynamicSvg';
import { SuggestedRisk } from '../types';
import { enumNameToInt } from '../util/enums';
import { useLoadingAction } from '../useLoadingAction';
import { AcceptedPill } from './AcceptedPill';
import { LabelChip } from './LabelChip';
import { PrimaryButton, SecondaryButton } from './SuggestionButtons';

const Card = styled(PageCard)<{ $bare?: boolean }>`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  padding: 20px;
  border-radius: 8px;

  ${({ $bare }) =>
    $bare &&
    `
    border: none;
    border-radius: 0;
  `}
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.stroke};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const asEnum = (e: object) => e as unknown as Record<string, string | number>;
const resolveLabel = (enumObj: object, getName: (v: number) => string, raw: string): string => {
  const code = enumNameToInt(asEnum(enumObj), raw);
  return code != null ? getName(code) : raw;
};

interface SuggestedScenarioCardProps {
  risk: SuggestedRisk;
  accepted: boolean;
  confirming: boolean;
  pending: boolean;
  /** Drop the card's own border/radius when it sits inside a merged result card. */
  bare?: boolean;
  onAccept: () => void;
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
  onTryAgain: () => void | Promise<unknown>;
}

export const SuggestedScenarioCard: React.FC<SuggestedScenarioCardProps> = ({
  risk,
  accepted,
  confirming,
  pending,
  bare,
  onAccept,
  onConfirm,
  onCancel,
  onTryAgain,
}) => {
  const { loadingKey, run } = useLoadingAction();
  const chips = [
    { label: 'Source', value: resolveLabel(RiskSources, getRiskSourcesName, risk.source) },
    { label: 'Event', value: resolveLabel(RiskEvents, getRiskEventsName, risk.event) },
    { label: 'Consequence', value: resolveLabel(RiskConsequences, getRiskConsequencesName, risk.consequence) },
  ];

  return (
    <Card $bare={bare}>
      <Eyebrow>
        <SparkleSvg size={24} />
        <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
          Suggested Risk Scenario
        </Poppins>
      </Eyebrow>
      <Spacer $px={16} />
      <Divider />
      <Spacer $px={16} />
      <Poppins px={16} weight={500} color="davysGrey">
        {risk.name}
      </Poppins>
      <Spacer $px={16} />
      <Chips>
        {chips.map((chip) => (
          <LabelChip key={chip.label} label={chip.label} value={chip.value} />
        ))}
      </Chips>
      <Spacer $px={16} />
      <Poppins px={16} color="cflowerBlue" css="line-height: 1.7;">
        {risk.narrative}
      </Poppins>
      <Spacer $px={16} />
      <Divider />
      <Spacer $px={16} />

      {accepted ? (
        <AcceptedPill />
      ) : confirming ? (
        <Actions>
          <Poppins px={14} weight={600} color="prussianBlue">
            Move to the next stage?
          </Poppins>
          <PrimaryButton
            label="Confirm"
            loading={loadingKey === 'confirm'}
            disabled={pending}
            onClick={() => run('confirm', async () => onConfirm())}
          />
          <SecondaryButton label="Cancel" disabled={pending} whiteBg onClick={onCancel} />
        </Actions>
      ) : (
        <Actions>
          <PrimaryButton label="Accept suggestion" disabled={pending} onClick={onAccept} />
          <SecondaryButton
            label="Try again"
            icon={<RefreshSvg size={16} />}
            loading={loadingKey === 'tryagain'}
            disabled={pending}
            whiteBg
            onClick={() => run('tryagain', async () => onTryAgain())}
          />
        </Actions>
      )}
    </Card>
  );
};
